import { Report } from '@/hooks/useGetReport';
import formatCurrency from '@/utils/formatCurrency';
import formatPrice from '@/utils/formatPrice';
import { FC, useEffect, useMemo, useState } from 'react';
import { IComparableProperty } from '../ComparableSales';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Slider } from '../ui/slider';
import { Textarea } from '../ui/textarea';
import { cn } from '@/lib/utils';
import _ from 'lodash';

export interface AIComparableProperty {
  FinalNotes?: string;
  Adjustments: {
    room: string;
    amount: number;
    images: string[];
  }[];
  PropertyA: {
    address: string;
    yearBuilt: number;
    soldPrice: string;
    evaluation: string;
  };
  PropertyB: {
    address: string;
    yearBuilt: number;
    soldPrice: string;
    evaluation: string;
  };
  adjustmentValue: number;
  notes?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mls: Record<string, any>;
  livingAreaAdjustment?: number;
  locationAdjustment?: number;
  garageAdjustment?: number;
  dateAdjustment?: number;
  apn: string;
  patio: boolean;
  pool: boolean;
  roofMaterial?: string;
  hvac?: string;
  constructionType?: string;
  basement: 'Yes' | 'No';
  lotSize: string;
  garageType: string;
  yearBuilt: number;
  bathrooms: number;
  bedrooms: number;
  pricePerSqft: number;
  livingArea: number;
  soldDate: string;
  soldPrice: number;
  address: string;
  id: string;
  priceAdjustmentAmount: number;
  priceAdjustmentDirection: 'increase' | 'decrease';
  priceAdjustmentExplanation: string;
}

interface Props {
  comparable: AIComparableProperty;
  report: Report;
  isCapture: boolean;
  onChangePrice: (value: number) => void;
}

const calculateSliderAdjustment = (
  sliderValue: number,
  soldPrice: number = 0,
  maxPercentage: number = 10,
): number => {
  const percentage = ((sliderValue - 50) / 50) * maxPercentage;
  return -Math.round((percentage / 100) * soldPrice);
};
const calculateDateOfSaleAdjustment = (
  sliderValue: number,
  soldPrice: number = 0,
): number => {
  const percentage = ((sliderValue - 50) / 50) * 10;
  return -Math.round((percentage / 100) * soldPrice);
};
const calculateLivingAreaAdjustment = (
  subjectSqft: number,
  compSqft: number,
  pricePerSqft: number,
): number => {
  const sqftDifference = subjectSqft - compSqft;
  return sqftDifference * pricePerSqft;
};

const getSliderPercentage = (amount?: number, soldPrice: number = 0) => {
  if (!amount) {
    return 50;
  }

  const range = soldPrice / 5;

  const offsetValue = amount + soldPrice / 10;

  const percentage = (offsetValue / range) * 100;

  return Math.round(percentage * 10) / 10;
};

const AIComparableReport: FC<Props> = ({
  comparable,
  report,
  isCapture,
  onChangePrice,
}) => {
  const [locationValues, setLocationValues] = useState(
    getSliderPercentage(-comparable?.locationAdjustment, comparable.soldPrice),
  );
  const [garageValues, setGarageValues] = useState(
    getSliderPercentage(-comparable?.garageAdjustment, comparable.soldPrice),
  );
  const [dateOfSaleValues, setDateOfSaleValues] = useState(
    getSliderPercentage(-comparable?.dateAdjustment, comparable.soldPrice),
  );
  // const [specialAdjustmentValues, setSpecialAdjustmentValues] = useState(50);
  const [notes, setNotes] = useState(comparable?.notes ?? '');
  const [pricePerSqft, setPricePerSqft] = useState(
    comparable?.pricePerSqft ?? 200,
  );

  const locationAdj = calculateSliderAdjustment(
    locationValues ?? 50,
    comparable.soldPrice,
  );
  const garageAdj = calculateSliderAdjustment(
    garageValues ?? 50,
    comparable.soldPrice,
  );
  const dateAdj = calculateDateOfSaleAdjustment(
    dateOfSaleValues ?? 50,
    comparable.soldPrice,
  );

  const livingAreaAdj = calculateLivingAreaAdjustment(
    report?.propertyInfo.livingSquareFeet,
    comparable.livingArea,
    pricePerSqft ?? 200,
  );

  const totalAdjustments = useMemo(
    () =>
      locationAdj +
      garageAdj +
      dateAdj +
      livingAreaAdj +
      comparable.priceAdjustmentAmount,
    [
      comparable.priceAdjustmentAmount,
      dateAdj,
      garageAdj,
      livingAreaAdj,
      locationAdj,
    ],
  );

  const finalAdjustedValue = useMemo(
    () => parseInt(comparable.soldPrice.toString(), 10) + totalAdjustments,
    [comparable.soldPrice, totalAdjustments],
  );

  useEffect(() => {
    onChangePrice(finalAdjustedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalAdjustedValue]);
  return (
    <Card className="shadow-lg">
      <CardHeader className="bg-accent/10">
        <div className="flex items-start justify-between">
          <CardTitle className="text-xl text-foreground">
            {comparable.address}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <div>
          <div className="overflow-x-auto">
            <div
              className={cn(
                'pb-4',
                isCapture ? 'grid grid-cols-4 gap-2' : 'flex gap-4',
              )}
              style={{ minWidth: 'max-content' }}
            >
              {comparable?.mls?.Media?.filter((_, idx) => idx <= 4)?.map(
                (photo, index) => (
                  <div
                    key={index}
                    className="w-40 h-28 overflow-hidden rounded-lg border border-border shadow-sm"
                  >
                    <img
                      src={photo.MediaURL}
                      alt=""
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      crossOrigin="anonymous"
                    />
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
        <Separator className="my-6" />

        {/* Property Details */}
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          <div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Living Area:</span>
                <span className="font-medium">{comparable.livingArea}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Year Built:</span>
                <span className="font-medium">{comparable.yearBuilt}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Lot Size:</span>
                <span className="font-medium">{comparable.lotSize}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Beds:</span>
                <span className="font-medium">{comparable.bedrooms}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Baths:</span>
                <span className="font-medium">{comparable.bathrooms}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Garage Size:</span>
                <span className="font-medium">
                  {comparable.mls?.GarageSpaces}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Last Sold:</span>
                <span className="font-medium">
                  {formatPrice(parseInt(comparable.soldDate, 10))}
                </span>
              </div>
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="bg-secondary/30 rounded-lg p-4">
              <p className="text-sm font-semibold text-foreground mb-1">
                Sold Price
              </p>
              <p className="text-3xl font-bold text-foreground mb-3">
                {formatPrice(comparable.soldPrice)}
              </p>
            </div>
          </div>
        </div>

        {/* AI Analysis Notes */}
        {comparable.notes && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-foreground mb-2">
              Notes:
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed bg-secondary/20 p-4 rounded-lg">
              {comparable.notes}
            </p>
          </div>
        )}

        <Separator className="my-6" />

        {/* User Adjustment Sliders */}
        <div className="space-y-6">
          <h4 className="text-lg font-semibold text-foreground">
            Additional Adjustments
          </h4>

          {/* Lot, Views & Location */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-sm font-medium">
                Lot, Views & Location
              </Label>
              <span
                className={`text-sm font-semibold ${
                  locationAdj >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {formatCurrency(locationAdj)}
              </span>
            </div>
            <Slider
              value={[locationValues ?? 50]}
              onValueChange={(value) => setLocationValues(value[0])}
              max={100}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Much Worse</span>
              <span>Same</span>
              <span>Much Better</span>
            </div>
          </div>

          {/* Garage Space, Outbuildings & Accessory Structures */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-sm font-medium">
                Garage Space, Outbuildings & Accessory Structures
              </Label>
              <span
                className={`text-sm font-semibold ${
                  garageAdj >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {formatCurrency(garageAdj)}
              </span>
            </div>
            <Slider
              value={[garageValues ?? 50]}
              onValueChange={(value) => setGarageValues(value[0])}
              max={100}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Much Worse</span>
              <span>Same</span>
              <span>Much Better</span>
            </div>
          </div>

          {/* Date Of Sale Adjustment */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-sm font-medium">
                Date Of Sale Adjustment
              </Label>
              <span
                className={`text-sm font-semibold ${
                  dateAdj >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {formatCurrency(dateAdj)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Have real estate prices gone up or down since this property sold?
            </p>
            <Slider
              value={[dateOfSaleValues ?? 50]}
              onValueChange={(value) => setDateOfSaleValues(value[0])}
              max={100}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Prices Have Increased</span>
              <span>Same</span>
              <span>Prices Have Decreased</span>
            </div>
          </div>

          {/* Special Adjustments */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Special Adjustments</Label>
            {isCapture ? (
              <p className="text-sm">{notes ?? 'N/A'}</p>
            ) : (
              <Textarea
                placeholder="Enter any special adjustments (e.g., This comparable had a pool)"
                value={notes ?? ''}
                onChange={(e) => setNotes(e.target.value)}
                className="min-h-[80px]"
              />
            )}
          </div>
          <div className="space-y-3">
            <Label className="text-sm font-medium">AI Analysis Summary</Label>
            {isCapture ? (
              <p className="text-sm">{comparable.priceAdjustmentExplanation}</p>
            ) : (
              <Textarea
                value={comparable.priceAdjustmentExplanation}
                // onChange={(e) => setNotes(e.target.value)}
                className="min-h-[80px]"
                disabled
              />
            )}
          </div>
          <div className="flex items-center gap-4">
            <Label className="text-sm font-medium">AI Price Adjustment:</Label>
            <p
              className={cn(
                'font-medium text-sm',
                comparable.priceAdjustmentDirection === 'increase'
                  ? 'text-green-600'
                  : 'text-red-600',
              )}
            >
              {formatCurrency(comparable.priceAdjustmentAmount)}
            </p>
          </div>

          {/* Living Square Footage Adjustment */}
          <div className="space-y-3 bg-secondary/20 p-4 rounded-lg">
            <Label className="text-sm font-semibold">
              Living Square Footage Adjustment
            </Label>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Price per sqft:
                </span>
                <div className="flex items-center">
                  <span className="text-sm mr-1">$</span>
                  {isCapture ? (
                    <p className="text-sm">{pricePerSqft}</p>
                  ) : (
                    <Input
                      type="number"
                      value={pricePerSqft ?? 200}
                      onChange={(e) => setPricePerSqft(Number(e.target.value))}
                      className="w-24 h-8"
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                Subject: {report?.propertyInfo.livingSquareFeet} sqft |
                Comparable: {comparable.livingArea} sqft
              </span>
              <span className="text-muted-foreground">
                Difference:{' '}
                {(
                  report?.propertyInfo.livingSquareFeet - comparable.livingArea
                ).toLocaleString()}{' '}
                sqft
              </span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t">
              <span className="text-sm font-medium">
                Living Area Adjustment:
              </span>
              <span
                className={`text-sm font-semibold ${
                  livingAreaAdj >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {formatCurrency(livingAreaAdj)}
              </span>
            </div>
          </div>
        </div>

        <Separator className="my-6" />

        {/* Final Adjusted Value Summary */}
        <div className="bg-primary/10 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-foreground mb-4">
            Adjusted Value Summary
          </h4>
          <div className="space-y-2 mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Sold Price:</span>
              <span className="font-medium">
                {formatPrice(parseInt(comparable.soldPrice.toString(), 10))}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                Lot, Views & Location:
              </span>
              <span
                className={`font-medium ${
                  locationAdj >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {formatCurrency(locationAdj)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                Garage & Outbuildings:
              </span>
              <span
                className={`font-medium ${
                  garageAdj >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {formatCurrency(garageAdj)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Date of Sale:</span>
              <span
                className={`font-medium ${
                  dateAdj >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {formatCurrency(dateAdj)}
              </span>
            </div>
            {/* <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                Special Adjustments:
              </span>
              <span
                className={`font-medium ${
                  specialAdj >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {formatCurrency(specialAdj)}
              </span>
            </div> */}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Living Area:</span>
              <span
                className={`font-medium ${
                  livingAreaAdj >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {formatCurrency(livingAreaAdj)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                AI Price Adjustment:
              </span>
              <span
                className={`font-medium ${
                  comparable.priceAdjustmentDirection === 'increase'
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}
              >
                {formatCurrency(comparable.priceAdjustmentAmount)}
              </span>
            </div>
          </div>
          <Separator className="my-3" />
          <div className="flex justify-between text-sm font-semibold">
            <span>Total Adjustments:</span>
            <span
              className={
                totalAdjustments >= 0 ? 'text-green-600' : 'text-red-600'
              }
            >
              {formatCurrency(totalAdjustments)}
            </span>
          </div>
          <div className="flex justify-between items-center mt-4 pt-4 border-t-2 border-primary/30">
            <span className="text-lg font-bold">Final Adjusted Value:</span>
            <span className="text-2xl font-bold text-primary">
              {formatPrice(finalAdjustedValue)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AIComparableReport;
